import { createContext, Dispatch, PropsWithChildren, useContext, useMemo, useReducer } from "react";
import { useSearchParams } from "react-router";
import { ArticleType } from "@shared/url";
import {
  AgeCategory,
  AnyArticleFilterInput,
  AnyArticleSortInput,
  RentalAccommodationArticleFilterInput,
  RentalAccommodationSortInput,
  SaleAccommodationArticleFilterInput,
  SaleAccommodationSortInput
} from "@shared/graphql";
import { useTools } from "../hooks/tools";
import { parse } from "../tools/search-params/search-criteria";

export type CustomRentalAccommodationFilterInput = Omit<RentalAccommodationArticleFilterInput, "guests">
  & { guests?: Record<AgeCategory, number> };

export type AnyArticleSearch = CustomRentalAccommodationFilterInput
  | Exclude<AnyArticleFilterInput, RentalAccommodationArticleFilterInput>;

export type AnySearchKeys = keyof CustomRentalAccommodationFilterInput
| keyof SaleAccommodationArticleFilterInput;

type ArticleTypeState = {
  forced?: Partial<AnyArticleSearch>;
  readonly mainCriteria: AnySearchKeys[];
  saving: AnyArticleSearch;
  sort: AnyArticleSortInput;
}

type State = Record<ArticleType | "sold", ArticleTypeState>;

type Action = { type: ArticleType | "sold", filter: AnyArticleSearch }
  | { type: ArticleType | "sold", sort: AnyArticleSortInput };

const SearchContext = createContext<[ State, Dispatch<Action> ] | undefined>(undefined);

const reducer = (state: State, action: Action): State => ({
  ...state,
  [action.type]: {
    ...state[action.type],
    ...("filter" in action) && { saving: action.filter },
    ...("sort" in action) && { sort: action.sort }
  }
});

const useInitialState = () => {

  const [ searchParams ] = useSearchParams();
  const { routingData } = useTools();

  const searchType = useMemo<ArticleType | undefined>(() => routingData("search")?.withSearchParams, []);

  return {
    [ArticleType.RENTAL]: {
      mainCriteria: [ "period", "objectTypes", "guests", "bedrooms" ] as AnySearchKeys[],
      saving: searchType === ArticleType.RENTAL
        ? parse(searchParams, searchType)
        : { kind: ArticleType.RENTAL } as CustomRentalAccommodationFilterInput,
      sort: { publication: "DESC" } as RentalAccommodationSortInput
    },
    [ArticleType.SALE]: {
      mainCriteria: [ "zips", "objectTypes", "priceRange" ] as AnySearchKeys[],
      forced: { sold: false },
      saving: searchType === ArticleType.SALE
        ? parse(searchParams, searchType)
        : { kind: ArticleType.SALE } as SaleAccommodationArticleFilterInput,
      sort: { publication: "DESC" } as SaleAccommodationSortInput
    },
    "sold": {
      mainCriteria: [ "zips", "objectTypes" ] as AnySearchKeys[],
      forced: { sold: true },
      saving: searchType === ArticleType.SALE
        ? parse(searchParams, searchType)
        : { kind: ArticleType.SALE } as SaleAccommodationArticleFilterInput,
      sort: { publication: "DESC" } as SaleAccommodationSortInput
    }
  };

}

export const useSearchContext = () => {
  const ctx = useContext(SearchContext);
  if (!ctx) throw new Error("useSearchContext must be used within a SearchProvider");
  return ctx;
}

export const SearchProvider = ({ children }: PropsWithChildren) => {

  const initialState = useInitialState();
  const [ state, dispatch ] = useReducer(reducer, initialState);

  return (
    <SearchContext value={[ state, dispatch ]}>
      {children}
    </SearchContext>);

}
